import { storableError } from '../../util/errors';
import { fetchSkillsAndToolsSuggestions } from '../../util/api';
import { util as sdkUtil } from '../../util/sdkLoader';

import { addMarketplaceEntities, getListingsById } from '../../ducks/marketplaceData.duck';
import { fetchSkillsAndTools } from '../../ducks/SkillsAndTools.duck';
import { fetchOffPlatformTestimonials, fetchOffPlatformReviews } from '../../util/api';
import { trackIntercomEvent, updateIntercomUser } from '../../util/intercom';

// ================ Action types ================ //

export const SEARCH_TALENT_REQUEST = 'app/BrowseFreelancersPage/SEARCH_TALENT_REQUEST';
export const SEARCH_TALENT_SUCCESS = 'app/BrowseFreelancersPage/SEARCH_TALENT_SUCCESS';
export const SEARCH_TALENT_ERROR = 'app/BrowseFreelancersPage/SEARCH_TALENT_ERROR';

export const GET_SKILLS_AND_TOOLS_SUGGESTIONS_REQUEST =
  'app/BrowseFreelancersPage/GET_SKILLS_AND_TOOLS_SUGGESTIONS_REQUEST';
export const GET_SKILLS_AND_TOOLS_SUGGESTIONS_SUCCESS =
  'app/BrowseFreelancersPage/GET_SKILLS_AND_TOOLS_SUGGESTIONS_SUCCESS';
export const GET_SKILLS_AND_TOOLS_SUGGESTIONS_ERROR =
  'app/BrowseFreelancersPage/GET_SKILLS_AND_TOOLS_SUGGESTIONS_ERROR';

export const QUERY_REVIEWS_REQUEST = 'app/ProfilePage/QUERY_REVIEWS_REQUEST';
export const QUERY_REVIEWS_SUCCESS = 'app/ProfilePage/QUERY_REVIEWS_SUCCESS';
export const QUERY_REVIEWS_ERROR = 'app/ProfilePage/QUERY_REVIEWS_ERROR';

export const QUERY_OTHER_LISTINGS_REQUEST = 'app/ProfilePage/QUERY_OTHER_LISTINGS_REQUEST';
export const QUERY_OTHER_LISTINGS_SUCCESS = 'app/ProfilePage/QUERY_OTHER_LISTINGS_SUCCESS';
export const QUERY_OTHER_LISTINGS_ERROR = 'app/ProfilePage/QUERY_OTHER_LISTINGS_ERROR';

export const QUERY_OFF_PLATFORM_TESTIMONIALS_REQUEST =
  'app/ProfilePage/QUERY_OFF_PLATFORM_TESTIMONIALS_REQUEST';
export const QUERY_OFF_PLATFORM_TESTIMONIALS_SUCCESS =
  'app/ProfilePage/QUERY_OFF_PLATFORM_TESTIMONIALS_SUCCESS';
export const QUERY_OFF_PLATFORM_TESTIMONIALS_ERROR =
  'app/ProfilePage/QUERY_OFF_PLATFORM_TESTIMONIALS_ERROR';

export const QUERY_OFF_PLATFORM_REVIEWS_REQUEST =
  'app/ProfilePage/QUERY_OFF_PLATFORM_REVIEWS_REQUEST';
export const QUERY_OFF_PLATFORM_REVIEWS_SUCCESS =
  'app/ProfilePage/QUERY_OFF_PLATFORM_REVIEWS_SUCCESS';
export const QUERY_OFF_PLATFORM_REVIEWS_ERROR = 'app/ProfilePage/QUERY_OFF_PLATFORM_REVIEWS_ERROR';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchTalentError: null,
  currentPageResultIds: [],
  currentPageReviews: [],
  getReviewsInProgress: false,
  queryReviewsError: null,
  otherListingRefs: [],
  queryOtherListingsError: null,
  skillsAndToolsSuggestions: [],
  getSkillsAndToolsSuggestionsInProgress: false,
  getSkillsAndToolsSuggestionsError: null,
  offPlatformTestimonials: [],
  queryOffPlatformTestimonialsError: null,
  offPlatformReviews: [],
  queryOffPlatformReviewsError: null,
};

const resultIds = data => data.data.map(l => l.id);

const reducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_TALENT_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchListingsError: null,
      };
    case SEARCH_TALENT_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case SEARCH_TALENT_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case GET_SKILLS_AND_TOOLS_SUGGESTIONS_REQUEST:
      return {
        ...state,
        skillsAndToolsSuggestions: [],
        getSkillsAndToolsSuggestionsInProgress: true,
      };
    case GET_SKILLS_AND_TOOLS_SUGGESTIONS_SUCCESS:
      return {
        ...state,
        skillsAndToolsSuggestions: payload.data,
        getSkillsAndToolsSuggestionsInProgress: false,
      };
    case GET_SKILLS_AND_TOOLS_SUGGESTIONS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        skillsAndToolsSuggestions: [],
        getSkillsAndToolsSuggestionsInProgress: false,
        getSkillsAndToolsSuggestionsError: storableError(payload),
      };
    case QUERY_REVIEWS_REQUEST:
      return { ...state, queryReviewsError: null, getReviewsInProgress: true };
    case QUERY_REVIEWS_SUCCESS:
      return {
        ...state,
        currentPageReviews: payload,
        getReviewsInProgress: false,
      };
    case QUERY_REVIEWS_ERROR:
      return {
        ...state,
        currentPageReviews: [],
        queryReviewsError: payload,
        getReviewsInProgress: false,
      };

    case QUERY_OFF_PLATFORM_TESTIMONIALS_REQUEST:
      return { ...state, queryOffPlatformTestimonialsError: null };
    case QUERY_OFF_PLATFORM_TESTIMONIALS_SUCCESS:
      return { ...state, offPlatformTestimonials: payload };
    case QUERY_OFF_PLATFORM_TESTIMONIALS_ERROR:
      return { ...state, offPlatformTestimonials: [], queryOffPlatformTestimonialsError: payload };

    case QUERY_OFF_PLATFORM_REVIEWS_REQUEST:
      return { ...state, queryOffPlatformReviewsError: null };
    case QUERY_OFF_PLATFORM_REVIEWS_SUCCESS:
      return { ...state, offPlatformReviews: payload };
    case QUERY_OFF_PLATFORM_REVIEWS_ERROR:
      return { ...state, offPlatformReviews: [], queryOffPlatformReviewsError: payload };

    case QUERY_OTHER_LISTINGS_REQUEST:
      return { ...state, queryOtherListingsError: null };
    case QUERY_OTHER_LISTINGS_SUCCESS:
      return {
        ...state,
        otherListingRefs: payload,
      };
    case QUERY_OTHER_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        queryOtherListingsError: storableError(payload),
      };
    default:
      return state;
  }
};

export default reducer;

// ================ Action creators ================ //

export const searchTalentRequest = searchParams => ({
  type: SEARCH_TALENT_REQUEST,
  payload: { searchParams },
});

export const searchTalentSuccess = response => ({
  type: SEARCH_TALENT_SUCCESS,
  payload: { data: response.data },
});

export const searchTalentError = e => ({
  type: SEARCH_TALENT_ERROR,
  error: true,
  payload: e,
});

export const getSkillsAndToolsSuggestionsRequest = () => ({
  type: GET_SKILLS_AND_TOOLS_SUGGESTIONS_REQUEST,
});
export const getSkillsAndToolsSuggestionsSuccess = suggestions => ({
  type: GET_SKILLS_AND_TOOLS_SUGGESTIONS_SUCCESS,
  payload: { data: suggestions },
});
export const getSkillsAndToolsSuggestionsError = e => ({
  type: GET_SKILLS_AND_TOOLS_SUGGESTIONS_ERROR,
  error: true,
  payload: e,
});

export const queryReviewsRequest = () => ({
  type: QUERY_REVIEWS_REQUEST,
});

export const queryReviewsSuccess = reviews => ({
  type: QUERY_REVIEWS_SUCCESS,
  payload: reviews,
});

export const queryReviewsError = e => ({
  type: QUERY_REVIEWS_ERROR,
  error: true,
  payload: e,
});

export const queryOffPlatformTestimonialsRequest = () => ({
  type: QUERY_OFF_PLATFORM_TESTIMONIALS_REQUEST,
});

export const queryOffPlatformTestimonialsSuccess = testimonials => ({
  type: QUERY_OFF_PLATFORM_TESTIMONIALS_SUCCESS,
  payload: testimonials,
});

export const queryOffPlatformTestimonialsError = e => ({
  type: QUERY_OFF_PLATFORM_TESTIMONIALS_ERROR,
  error: true,
  payload: e,
});

export const queryOffPlatformReviewsRequest = () => ({
  type: QUERY_OFF_PLATFORM_REVIEWS_REQUEST,
});

export const queryOffPlatformReviewsSuccess = reviews => ({
  type: QUERY_OFF_PLATFORM_REVIEWS_SUCCESS,
  payload: reviews,
});

export const queryOffPlatformReviewsError = e => ({
  type: QUERY_OFF_PLATFORM_REVIEWS_ERROR,
  error: true,
  payload: e,
});

export const queryOtherListingsRequest = () => ({
  type: QUERY_OTHER_LISTINGS_REQUEST,
});

export const queryOtherListingsSuccess = listings => ({
  type: QUERY_OTHER_LISTINGS_SUCCESS,
  payload: listings,
});

export const queryOtherListingsError = e => ({
  type: QUERY_OTHER_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const searchProfileListings = searchParams => (dispatch, getState, sdk) => {
  dispatch(searchTalentRequest(searchParams));
  dispatch(queryReviewsRequest());

  const state = getState();
  const skills = state.SkillsAndTools.skillOptions;
  const tools = state.SkillsAndTools.toolOptions;

  const fetchSkillsAndToolsIfNeeded = () => {
    if (skills.length === 0 || tools.length === 0) {
      return dispatch(fetchSkillsAndTools());
    }
    return Promise.resolve();
  };

  // Server rendering serializes the store once loadData settles: wait for the
  // skill/tool lists too, without holding back the listing work.
  const skillsAndToolsPromise = fetchSkillsAndToolsIfNeeded();

  const listingsPromise = sdk.listings
    .query(searchParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(searchTalentSuccess(response));
      return response;
    })
    .then(async response => {
      const pageListings = getListingsById(
        getState(),
        getState().BrowseFreelancersPage.currentPageResultIds
      );

      const uniqueAuthorIds = new Set(pageListings.map(l => l.author.id.uuid));
      const uniqueAuthorIdsArray = Array.from(uniqueAuthorIds);

      const userReviewsPromises = uniqueAuthorIdsArray.map(id => {
        return sdk.reviews
          .query({
            subject_id: id,
            state: 'public',
            include: ['subject'],
          })
          .then(userReview => userReview.data.data);
      });

      const userOtherListingsPromises = uniqueAuthorIdsArray.map(id => {
        return sdk.listings
          .query({
            author_id: id,
            pub_listingType: ['service', 'portfolioWork'],
            include: ['author', 'images'],
            'fields.image': [
              // Listing page
              'variants.landscape-crop',
              'variants.landscape-crop2x',
              'variants.landscape-crop4x',
              'variants.landscape-crop6x',
              'variants.horizontal',
              'variants.horizontal-2x',
              'variants.vertical',
              'variants.vertical-2x',
              'variants.square',
              'variants.square2x',

              // Avatars
              'variants.square-small',
              'variants.square-small2x',
              // Social media
              'variants.facebook',
              'variants.twitter',
            ],
            'imageVariant.horizontal': sdkUtil.objectQueryString({
              w: 1920,
              h: 1080,
              fit: 'scale',
            }),
            'imageVariant.horizontal-2x': sdkUtil.objectQueryString({
              w: 2560,
              h: 1440,
              fit: 'scale',
            }),
            'imageVariant.vertical': sdkUtil.objectQueryString({
              w: 1080,
              h: 1920,
              fit: 'scale',
            }),
            'imageVariant.vertical-2x': sdkUtil.objectQueryString({
              w: 1440,
              h: 2560,
              fit: 'scale',
            }),
            'imageVariant.square': sdkUtil.objectQueryString({
              w: 1080,
              h: 1080,
              fit: 'scale',
            }),
            'imageVariant.square2x': sdkUtil.objectQueryString({
              w: 1440,
              h: 1440,
              fit: 'scale',
            }),
          })
          .then(userOtherListingsResponse => {
            // Add marketplace entities for each response
            dispatch(addMarketplaceEntities(userOtherListingsResponse));
            return userOtherListingsResponse.data.data;
          });
      });

      const userOffPlatformTestimonialsPromises = uniqueAuthorIdsArray.map(id => {
        return fetchOffPlatformTestimonials(id)
          .then(response => response.items || [])
          .catch(error => {
            console.error(`Error fetching off-platform testimonials for user ${id}:`, error);
            return [];
          });
      });

      const userOffPlatformReviewsPromises = uniqueAuthorIdsArray.map(id => {
        return fetchOffPlatformReviews(id)
          .then(response => response.items || [])
          .catch(error => {
            console.error(`Error fetching off-platform reviews for user ${id}:`, error);
            return [];
          });
      });

      const userReviews = await Promise.all(userReviewsPromises);
      const userOtherListings = await Promise.all(userOtherListingsPromises);
      const userOffPlatformTestimonials = await Promise.all(userOffPlatformTestimonialsPromises);
      const userOffPlatformReviews = await Promise.all(userOffPlatformReviewsPromises);

      // Flatten the array of arrays and pick only the id and type properties
      const flattenedOtherListings = userOtherListings.flat();
      const otherListingRefs = flattenedOtherListings.map(({ id, type }) => ({ id, type }));

      dispatch(queryReviewsSuccess(userReviews.flat()));
      dispatch(queryOtherListingsSuccess(otherListingRefs));
      dispatch(queryOffPlatformTestimonialsSuccess(userOffPlatformTestimonials.flat()));
      dispatch(queryOffPlatformReviewsSuccess(userOffPlatformReviews.flat()));

      if (typeof window === 'object') {
        // Helper functions for normalizing search_filters
        const norm = s =>
          (s || '')
            .toString()
            .trim()
            .toLowerCase();
        const csv = v => {
          const arr = (v || '')
            .split(',')
            .map(norm)
            .filter(Boolean);
          return [...new Set(arr)].sort().join(',') || undefined;
        };

        // Normalize search parameters for search_filters only
        const kw = norm(searchParams?.keywords);
        const skills = csv(searchParams?.skills);
        const tools = csv(searchParams?.tools);
        const language = csv(searchParams?.language);
        const hobbies = csv(searchParams?.hobbies);
        const hourlyRate = norm(searchParams?.hourlyRate);

        // Build search_filters in GA4-friendly format
        const parts = [];
        if (kw) parts.push(`kw:${kw}`);
        if (skills) parts.push(`skills:${skills}`);
        if (tools) parts.push(`tools:${tools}`);
        if (language) parts.push(`language:${language}`);
        if (hobbies) parts.push(`hobbies:${hobbies}`);
        if (hourlyRate) parts.push(`hourlyRate:${hourlyRate}`);
        const search_filters = parts.join('|') || 'none';

        const search_count = response?.data?.meta?.totalItems || 0;

        window.dataLayer.push({
          event: 'view_item_list',
          content_type: 'freelancer',
          search_term: searchParams?.keywords,
          search_skills: searchParams?.skills,
          search_tools: searchParams?.tools,
          search_filters: search_filters,
          search_count: search_count,
          ecommerce: {
            items: pageListings.map((listing, index) => {
              return {
                item_name: listing.author.attributes.profile.displayName,
                item_id: listing.author.id.uuid,
                item_brand: listing.author.id.uuid,
                item_list_name: 'search_results',
                item_list_id: '' + searchParams.page || '1',
                index: index + 1,
                quantity: '1',
              };
            }),
          },
        });
        if (search_filters !== 'none') {
          trackIntercomEvent('Talentpage query', {
            search_term: searchParams?.keywords,
            search_skills: searchParams?.skills,
            search_tools: searchParams?.tools,
            search_filters: search_filters,
            search_count: search_count,
          });
          updateIntercomUser({
            talentpageQuery: true,
          });
        }
      }

      return response;
    })
    .catch(error => {
      console.error('Error in searchProfileListings:', error);
      dispatch(searchTalentError(error));
      throw error;
    });

  return Promise.all([listingsPromise, skillsAndToolsPromise]).then(([response]) => response);
};

export const getSkillsAndToolsSuggestions = searchTerm => (dispatch, getState, sdk) => {
  dispatch(getSkillsAndToolsSuggestionsRequest());
  return fetchSkillsAndToolsSuggestions(searchTerm)
    .then(response => {
      dispatch(getSkillsAndToolsSuggestionsSuccess(response));
    })
    .catch(error => {
      dispatch(getSkillsAndToolsSuggestionsError(error));
    });
};
